import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { NdmsAuthService } from './shared/core-ndms/services/ndms-auth.service';
import { NdmsRouteResolverGuard } from 'projects/shared/core/services/ndms-route-resolver.guard';
import { NdmsSafeSubscriber } from 'projects/shared/core/interfaces/ndms-safe-subscriber';
import { LogoffTimerComponent } from './components/logoff-timer/logoff-timer.component';
import { LoginInformationModel } from 'projects/shared/core/app/api/models/login-information-model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent extends NdmsSafeSubscriber {
  trainingMode: boolean;
  timeoutModal: BsModalRef;
  
  timeoutWarning: number = 60;
  inactivityCounter: number = 0;
  inactivityTimeout: number = 0;

  idleState: string = "Not started.";
  timedOut: boolean = false;
  lastPing: Date = null;

  get preloadIndicator() {
    return NdmsRouteResolverGuard.PreloadIndicator;
  }

  constructor(
    private _idle: Idle,
    private _keepalive: Keepalive,
    private _ndmsAuthService: NdmsAuthService,
    private _modalService: BsModalService,
    private _title: Title,
    @Inject('SITE_MODE') public siteMode: string,
    @Inject('BASE_URL') public baseUrl: string,
    @Inject('PROGRAM') public program: string,
    private _router: Router) {
    super();

    this._title.setTitle("Home - NDMS Suite");
    this._ndmsAuthService.authFinished.subscribe(data => {
      this.trainingMode = siteMode === 'train';
      if (data.lastName) {
        this.safeSubscribe(this._ndmsAuthService.getLoginInformation(), info => {
          let lastLoggedIn = new Date(info.lastLoggedIn);
          let lastLoggedInTicks = new Date(lastLoggedIn).getTime();
          if (info.sessionTimeout > 0) {
            this.inactivityTimeout = info.sessionTimeout;
          }
          this.lastPing = new Date(window.localStorage.getItem("keep_alive"));
          let lastPingTicks = this.lastPing.getTime();
          let currentTicks = new Date().getTime();
          if (lastLoggedInTicks < lastPingTicks && (currentTicks - lastPingTicks) > (this.inactivityTimeout + this.timeoutWarning) * 1000) {
            this.logout();
          }
          else {
            this.setSessionIdle();
            this.idleReset();
          }
        }, error => console.log("error: ", error));
      }
    });
    
    this._router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this._ndmsAuthService.setRedirectUrl(event.url);
      }
    });
  }

  ngAfterViewInit() {
    NdmsRouteResolverGuard.PreloadIndicator = true;
  }

  showTimeoutModal(){
    this.timeoutModal = this._modalService.show(LogoffTimerComponent, { backdrop: 'static', class: 'gray modal-lg' });
    this.safeSubscribe(this.timeoutModal.content.continue, () => {
      this.idleReset();
      this.timeoutModal.hide();
    });
    this.safeSubscribe(this.timeoutModal.content.logout, () => {
      this.logout();
      this.timeoutModal.hide();
    });
    setInterval(() => {
      this.timeoutModal.content.counter = this.inactivityCounter;
    }, 1000);
  }
    
  private idleReset() {
    this._idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  private logout() {
    window.localStorage.setItem("redirect_url", this._router.url);
    this._ndmsAuthService.logout(false);
  }

  private setSessionIdle() {
    this._idle.setIdle(this.inactivityTimeout);
    this._idle.setTimeout(this.timeoutWarning);
    this._idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this._keepalive.interval(15);
    this.lastPing = new Date();
    window.localStorage.setItem("keep_alive", this.lastPing.toLocaleString());
    
    this._idle.onIdleStart.subscribe(() => {
        this.idleState = 'You\'ve gone idle!'
        this.showTimeoutModal();
    });
    
    this._idle.onIdleEnd.subscribe(() => { 
      this.idleState = "No longer idle.";
      this.idleReset();
    });
    
    this._idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
      this.logout();
    });
    
    this._idle.onTimeoutWarning.subscribe((seconds) => {
      this.inactivityCounter = seconds;
    });

    this._keepalive.onPing.subscribe(() => {
      this.lastPing = new Date();
      window.localStorage.setItem("keep_alive", this.lastPing.toLocaleString());
    });
  }
  
}
